/**
 * <photo-globe> — interactive WebGL globe with customer photo markers.
 *
 * Reads photo data from an inner <script type="application/json" data-photos>.
 * Dynamically imports globe.gl from a CDN on first activation.
 * Auto-rotates by default (gated by data-auto-rotate and prefers-reduced-motion);
 * pauses while a marker is hovered (fine pointer only); stops permanently on
 * the first user gesture. Clicking a marker opens the shared lightbox dialog
 * referenced by data-target-selector.
 */

import { populateLightbox, pauseLightboxMedia } from '@theme/photo-lightbox';

// globe.gl pinned to a known-good version. Bumping requires re-checking
// that the private `_destructor` shutdown call below still exists.
const GLOBE_IMPORT = 'https://esm.sh/globe.gl@2.27.3';
const EARTH_TEXTURE = 'https://unpkg.com/three-globe/example/img/earth-blue-marble.jpg';
const EARTH_TOPOLOGY = 'https://unpkg.com/three-globe/example/img/earth-topology.png';

class PhotoGlobe extends HTMLElement {
  #globe = null;
  #mount = null;
  #photos = [];
  #resizeObserver = null;
  #initialized = false;
  #initPromise = null;
  #intersectionObserver = null;
  #currentLat = 20;
  #currentAltitude = 1.7;
  // Globe auto-rotates by default, pauses while the pointer is over the
  // mount (fine pointer only), resumes on leave. Effective auto-rotate =
  // #autoRotateAllowed && #wantAutoRotate && !#hoveringGlobe.
  #wantAutoRotate = false;
  #hoveringGlobe = false;
  #autoRotateAllowed = false;

  connectedCallback() {
    this.#mount = this.querySelector('[data-globe-mount]');
    if (!this.#mount) return;

    this.#photos = this.#readPhotos();
    if (this.#photos.length === 0) return;

    // Lazy-init: only load globe.gl when the element enters the viewport
    // AND its panel is visible (tab is active). The panel starts
    // visible so this fires on first paint for the default-active globe tab.
    if ('IntersectionObserver' in window) {
      this.#intersectionObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#intersectionObserver.disconnect();
            this.#intersectionObserver = null;
            this.#init();
            break;
          }
        }
      }, { rootMargin: '200px' });
      this.#intersectionObserver.observe(this);
    } else {
      this.#init();
    }
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    if (this.#globe) {
      // _destructor is private API — see version-pin comment at top of file.
      try { this.#globe._destructor?.(); } catch (_) { /* noop */ }
      this.#globe = null;
    }
  }

  #readPhotos() {
    const script = this.querySelector('script[type="application/json"][data-photos]');
    if (!script) return [];
    try {
      const data = JSON.parse(script.textContent);
      return Array.isArray(data) ? data.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng)) : [];
    } catch (_) {
      return [];
    }
  }

  async #init() {
    if (this.#initialized || this.#initPromise) return this.#initPromise;
    this.setAttribute('data-loading', '');

    this.#initPromise = (async () => {
      let GlobeCtor;
      try {
        const module = await import(/* @vite-ignore */ GLOBE_IMPORT);
        GlobeCtor = module.default || module.Globe || module;
      } catch (err) {
        this.removeAttribute('data-loading');
        this.#renderFallback();
        console.warn('[photo-globe] Failed to load globe.gl', err);
        return;
      }

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.#autoRotateAllowed = this.dataset.autoRotate === 'true' && !reduceMotion;
      this.#wantAutoRotate = this.#autoRotateAllowed;

      const globe = new GlobeCtor(this.#mount)
        .globeImageUrl(EARTH_TEXTURE)
        .bumpImageUrl(EARTH_TOPOLOGY)
        .backgroundColor('rgba(0,0,0,0)')
        .atmosphereColor('#ffffff')
        .atmosphereAltitude(0.18)
        .htmlElementsData(this.#groupByLocation(this.#photos))
        .htmlLat('lat')
        .htmlLng('lng')
        .htmlAltitude(0.01)
        .htmlElement((photo) => this.#createMarker(photo));

      // Set renderer size to container
      this.#syncSize(globe);
      this.#resizeObserver = new ResizeObserver(() => this.#syncSize(globe));
      this.#resizeObserver.observe(this.#mount);

      globe.pointOfView({ lat: this.#currentLat, lng: 0, altitude: this.#currentAltitude }, 0);

      const controls = globe.controls();
      controls.enableZoom = false;
      controls.autoRotateSpeed = 0.4;

      this.#globe = globe;
      this.#initialized = true;
      this.removeAttribute('data-loading');
      this.#applyAutoRotate();

      // Pause rotation while the pointer is anywhere over the globe (sphere
      // or marker), resume on leave. Fine-pointer only — on touch,
      // pointerenter fires on tap and would just-barely pause every tap.
      if (window.matchMedia('(pointer: fine)').matches) {
        this.#mount.addEventListener('pointerenter', () => {
          this.#hoveringGlobe = true;
          this.#applyAutoRotate();
        });
        this.#mount.addEventListener('pointerleave', () => {
          this.#hoveringGlobe = false;
          this.#applyAutoRotate();
        });
      }
    })();

    return this.#initPromise;
  }

  #syncSize(globe) {
    const { clientWidth, clientHeight } = this.#mount;
    if (clientWidth > 0 && clientHeight > 0) {
      globe.width(clientWidth).height(clientHeight);
    }
  }

  #applyAutoRotate() {
    if (!this.#globe) return;
    this.#globe.controls().autoRotate =
      this.#autoRotateAllowed && this.#wantAutoRotate && !this.#hoveringGlobe;
  }

  /**
   * Collapse photos sharing a coordinate into a single marker with a count.
   * Multiple submissions from the same city would otherwise stack at one
   * point and only the topmost would be clickable.
   */
  #groupByLocation(photos) {
    const groups = new Map();
    for (const photo of photos) {
      const key = `${photo.lat.toFixed(2)},${photo.lng.toFixed(2)}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(photo);
    }
    return Array.from(groups.values()).map((bucket) => ({
      ...bucket[0],
      _groupCount: bucket.length,
      _groupPhotos: bucket,
    }));
  }

  #createMarker(photo) {
    const marker = document.createElement('button');
    marker.type = 'button';
    marker.className = 'photo-globe__marker';
    let label = `${photo.name} — ${photo.location}`;
    if (photo._groupCount > 1) label += `, ${photo._groupCount} photos from this location`;
    marker.setAttribute('aria-label', label);
    marker.style.setProperty('--marker-image', `url(${photo.thumb})`);

    if (photo._groupCount > 1) {
      const badge = document.createElement('span');
      badge.className = 'photo-globe__marker-badge';
      badge.setAttribute('aria-hidden', 'true');
      badge.textContent = photo._groupCount > 9 ? '9+' : String(photo._groupCount);
      marker.appendChild(badge);
    }

    marker.addEventListener('click', (e) => {
      e.stopPropagation();
      this.#openLightbox(photo);
    });

    return marker;
  }

  #openLightbox(photo) {
    const targetSelector = this.dataset.targetSelector;
    if (!targetSelector) {
      console.warn('[photo-globe] no target-selector set');
      return;
    }
    const dialog = document.querySelector(targetSelector);
    if (!dialog) {
      console.warn('[photo-globe] dialog not found for', targetSelector);
      return;
    }
    const host = dialog.closest('dialog-component');
    if (!host) {
      console.warn('[photo-globe] dialog-component host missing');
      return;
    }

    // For grouped pins, pass the entire bucket so the lightbox can wire
    // prev/next nav. Single-photo pins fall back to a one-element array.
    // The bucket photos already carry the full lightbox shape (parsed from
    // the data-photos JSON), so hand them over as-is; populateLightbox reads
    // named props and ignores the extra _group* keys.
    const group = photo._groupPhotos || [photo];
    populateLightbox(dialog, group, 0);

    host.addEventListener('dialog:close', () => pauseLightboxMedia(dialog), { once: true });

    if (typeof host.showDialog === 'function') host.showDialog();
  }

  #renderFallback() {
    // If globe.gl fails to load, show a simple message and rely on the
    // gallery tab for the full photo list.
    const fallback = document.createElement('p');
    fallback.className = 'photo-globe__fallback';
    fallback.textContent = 'Globe view unavailable. Switch to the gallery view below.';
    this.#mount.appendChild(fallback);
  }
}

if (!customElements.get('photo-globe')) {
  customElements.define('photo-globe', PhotoGlobe);
}
